'use client';

import toast, { type Toast } from 'react-hot-toast';

import { cn } from '@/lib/utils';

/* react-hot-toast stays the engine (queue, positioning, dedupe by id), but every
   pixel it would draw is replaced: `toast.custom` hands us the bare positioned
   slot and we render the Cultiv card into it. None of the package's default
   styling — the white pill, the filled 20px status circle, the scale bounce —
   reaches the screen anymore. */

type Status = 'success' | 'error' | 'info' | 'loading';

/* The <Toaster> lives in the ROOT layout, outside NextIntlClientProvider, so the
   card cannot read the dictionary through a hook. The locale Providers pushes the
   copy in here once on mount instead; every emit reads it lazily, so a toast fired
   after hydration always gets the right locale. Individual calls can still override
   via options.closeLabel. */
const toastCopy = { close: 'Close' };

export const setToastCopy = (copy: { close?: string }) => {
  if (copy.close) toastCopy.close = copy.close;
};

/* The hairline is carried in the same box-shadow stack as the elevation rather
   than as a border, so it costs no layout box and stays crisp under transforms.
   Error is the one status that tints the outline; the rest share the carbon one. */
const NEUTRAL_RING =
  'shadow-[0_1px_2px_rgba(40,40,40,.08),0_8px_24px_-10px_rgba(40,40,40,.28),0_0_0_1px_rgba(40,40,40,.1)]';

const STATUS = {
  success: { chip: 'bg-[#3E8E5A]/[0.14] text-[#2F7549]', ring: NEUTRAL_RING },
  error: {
    chip: 'bg-[#C8371D]/[0.13] text-[#C8371D]',
    ring: 'shadow-[0_1px_2px_rgba(40,40,40,.08),0_8px_24px_-10px_rgba(200,55,29,.3),0_0_0_1px_rgba(200,55,29,.32)]',
  },
  info: { chip: 'bg-[#282828]/[0.08] text-[#282828]/60', ring: NEUTRAL_RING },
  loading: { chip: '', ring: NEUTRAL_RING },
} satisfies Record<Status, { chip: string; ring: string }>;

const Glyph = ({ status }: { status: Status }) => {
  if (status === 'loading') {
    return (
      <span className="mt-px h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-[#282828] border-r-transparent opacity-35" />
    );
  }

  const path =
    status === 'success' ? (
      <path d="M20 6 9 17l-5-5" strokeWidth={3.2} strokeLinejoin="round" />
    ) : status === 'error' ? (
      <>
        <path d="M12 7v6" strokeWidth={3} />
        <path d="M12 17h.01" strokeWidth={3} />
      </>
    ) : (
      <>
        <path d="M12 11v6" strokeWidth={3} />
        <path d="M12 7h.01" strokeWidth={3} />
      </>
    );

  const size = status === 'success' ? 12 : 13;

  return (
    <span
      className={cn(
        '-mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
        STATUS[status].chip
      )}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        aria-hidden="true"
      >
        {path}
      </svg>
    </span>
  );
};

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface CardProps {
  t: Toast;
  status: Status;
  title: string;
  detail?: string;
  action?: ToastAction;
  dismissible?: boolean;
  closeLabel: string;
}

function Card({ t, status, title, detail, action, dismissible, closeLabel }: CardProps) {
  const close = () => toast.dismiss(t.id);

  return (
    <div
      className={cn(
        'pointer-events-auto flex max-w-[420px] items-start gap-2.5 rounded-md bg-white py-2.5 pl-3 pr-2.5',
        'text-[13.5px] leading-[1.35] text-[#282828]',
        STATUS[status].ring,
        t.visible ? 'kq-toast-in' : 'kq-toast-out'
      )}
      role={status === 'error' ? 'alert' : 'status'}
    >
      <Glyph status={status} />

      <span className="min-w-0">
        <span className="kq-cap block font-semibold tracking-[-0.005em]">{title}</span>
        {detail && (
          <span className="mt-[5px] block text-[13px] leading-[1.45] text-[#282828]/60">
            {detail}
          </span>
        )}
      </span>

      {/* The card is items-start so a long detail cannot drag the actions down with it,
          which leaves this group aligned to the top of a box as tall as its 30px button
          — six pixels below the title's 18px line. The negative margin takes the hover
          target out of the layout height so the group centres on that first line; the
          fixed height keeps that true when only the 22px close button is present. */}
      {(action || dismissible) && (
        <span className="-my-1.5 ml-auto flex h-[30px] shrink-0 items-center gap-0.5 pl-3">
          {action && (
            <button
              type="button"
              onClick={() => {
                action.onClick();
                close();
              }}
              className="kq-cap h-[30px] cursor-pointer rounded-[5px] px-2.5 text-[12.5px] font-semibold text-[#E9551B] hover:bg-[#E9551B]/[0.08]"
            >
              {action.label}
            </button>
          )}

          {action && dismissible && <span className="mx-1 w-px self-stretch bg-[#282828]/[0.12]" />}

          {dismissible && (
            <button
              type="button"
              onClick={close}
              aria-label={closeLabel}
              className="flex h-[22px] w-[22px] shrink-0 cursor-pointer items-center justify-center rounded p-0 text-[#282828]/40 hover:bg-[#282828]/[0.06] hover:text-[#282828]"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </span>
      )}
    </div>
  );
}

interface ToastOptions {
  detail?: string;
  action?: ToastAction;
  duration?: number;
  /** Reuse an id to swap a toast in place (loading -> success) instead of stacking. */
  id?: string;
  /** Accessible name for the close button. Pass the localised string from the dictionary. */
  closeLabel?: string;
}

const emit = (status: Status, title: string, options: ToastOptions = {}, fallbackDuration: number) =>
  toast.custom(
    t => (
      <Card
        t={t}
        status={status}
        title={title}
        detail={options.detail}
        action={options.action}
        dismissible={status === 'error' || !!options.action}
        closeLabel={options.closeLabel ?? toastCopy.close}
      />
    ),
    {
      duration: options.duration ?? (options.action ? 6000 : fallbackDuration),
      id: options.id,
    }
  );

export const cultivToast = {
  success: (title: string, options?: ToastOptions) => emit('success', title, options, 2500),
  info: (title: string, options?: ToastOptions) => emit('info', title, options, 4000),
  error: (title: string, options?: ToastOptions) => emit('error', title, options, 5000),
  loading: (title: string, options?: ToastOptions) =>
    emit('loading', title, { ...options, duration: Infinity }, Infinity),
  dismiss: toast.dismiss,
};
